import React, { useContext, useState } from 'react';
import { Handle, Position } from 'reactflow';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { MyContext } from '../../context/myContext';

const CustomNode = ({ data }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [fetchedData, setFetchedData] = useState(null);
  const {selectedProduct} = useContext(MyContext);

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setFetchedData(null); // Clear fetched data when closing
  };

  const open = Boolean(anchorEl);

  const fetchData = async (nodeId) => {
    try {
      const response =await data.apiUrl.request(nodeId,selectedProduct);
      const result = response?.data;
      setFetchedData(result.stateDetails);
    } catch (error) {
      console.error('Fetch error:', error);
      setFetchedData([{ error: 'Failed to fetch data' }]); // Handle error 
    }
  };
  // Highlighted styles
  const getHighlightedStyle = (id, highlights) => {
    const index = highlights.indexOf(id);
    const isEven = index % 2 === 0;
  
    return {
      backgroundColor: '#f1f8ff',
      border: `3px solid ${isEven ? '#388E3C' : '#1976D2'}`,
      color: '#333',
    };
  };
  
  const highlightedStyle = getHighlightedStyle(data.node_id, data?.highlights || []);
  // Default style
  const defaultStyle = {
    backgroundColor:'#f1f8ff', // Blue for main branch, light blue for repo nodes
    color:'#333',
  };

  return (
    <div
      style={{
        ...{
          position: 'relative',
          padding: '2px 18px',
          borderRadius: '12px',
          boxShadow: '0 4.8px 7.2px rgba(0, 0, 0, 0.15)',
          fontFamily: 'Arial, sans-serif',
          maxWidth: '300px',
          maxHeight: '108px',
          display: 'flex',
          flexDirection: 'column',
          height: 'auto',
          boxSizing: 'border-box',
          marginBottom: '30px',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        },
        ...(data.highlighted ? highlightedStyle : defaultStyle) // Apply highlighted style if needed
      }}
      className="custom-node"
      role="region"
      aria-label="Custom node"
    >
      {data.repo && (
        <div
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '-3px',
            fontSize: '18px',
            color: '#666',
            backgroundColor: 'transparent',
            padding: '2.4px',
            borderRadius: '1.8px',
            fontStyle: 'italic',
            maxWidth: '54%',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
          }}
        >
          {data.repo}
        </div>
      )}
      <div
        style={{
          fontWeight: 'bold',
          fontSize: '18px',
          marginBottom: '3px',
          overflowWrap: 'break-word',
        }}
      >
        {data.state}
      </div>

      {data.timestamp_utc && Array.isArray(data.timestamp_utc) && (
        <ul
          style={{
            fontSize: '18px',
            padding: '0',
            margin: '0',
            listStyleType: 'none',
            color: data.repo ? '#555' : '#d3e0ff',
            overflowY: 'auto',
            maxHeight: '72px',
            scrollbarWidth: 'thick',
            msOverflowStyle: 'auto',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {data.timestamp_utc.map((item, index) => {
  const timestamp = item.timestamp;
  const nodeId = item.nodeId; // Get nodeId from the timestamp object
  const dateObj = new Date(timestamp);

  const istDate = new Date(dateObj.getTime() + (5.5 * 60 * 60 * 1000)); 

  const formattedDate = istDate.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const formattedTime = istDate
    .toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    })
    .replace(/am|pm/, (match) => match.toUpperCase()); 

  // Check if the timestamp matches the highlightedTimeStamp
  const isHighlighted = item === data.highlightedTimeStamp;  

  return (
    <li key={index}>
      <Button
        variant="text"
        style={{
          padding: '0',
          textTransform: 'none',
          color:'grey' ,
          width: '100%',
          fontWeight: isHighlighted ? 'bold' : '',
          fontSize: isHighlighted ? '18px': '15px',
        }}
        onClick={(event) => {
          fetchData(nodeId); // Use nodeId to fetch data
          handlePopoverOpen(event); // Pass the event here
        }}
      >
       {formattedDate} {formattedTime}
      </Button>
    </li>
  );
})}

        </ul>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          style: {
            padding: '6px',
            backgroundColor: '#fefef0',
            boxShadow: '0px 4.8px 9px rgba(0, 0, 0, 0.1)',
            borderRadius: '7.2px',
            border: '0.6px solid #e0e0e0',
          },
        }}
      >
        {fetchedData ? (
          fetchedData.length > 0 ? (
            fetchedData.map((item, index) => (
              <div key={index}>
                {Object.entries(item).map(([key, value]) => (
                  !["timestamp_utc", "id", "repo", "work_item_id", "event","is_major_release", "product_id"].includes(key) && (
                    <Typography key={key} variant="body2" style={{ marginBottom: '2.4px' }}>
                      <strong>{key}:</strong> {value !== null ? value.toString() : 'N/A'}
                    </Typography>
                  )
                ))}
              </div>
            ))
          ) : (
            <Typography variant="body2" style={{ color: '#555' }}>
              No data available.
            </Typography>
          )
        ) : (
          <Typography variant="body2" style={{ color: '#555' }}>
            Loading data...
          </Typography>
        )}
      </Popover>

      <Handle type="target" position={Position.Top} style={{ backgroundColor: 'transparent' }} />
      <Handle type="source" position={Position.Bottom} style={{ backgroundColor: 'transparent' }} />
      <Handle type="target" position={Position.Right} id="right" style={{ backgroundColor: 'transparent' }} />
      <Handle type="source" position={Position.Right} id="right" style={{ backgroundColor: 'transparent' }} />
      <Handle type="target" position={Position.Bottom} id="bottom" style={{ backgroundColor: 'transparent' }} />
    </div>
  );
};

export default CustomNode;
